import { Vue } from 'vue-property-decorator';
function getFocusableElementsIn(el) {
    // Get all focusable elements within the container
    return Array.from(el.querySelectorAll('button, [href], input, select, textarea, div[tabindex]')).filter((element) => {
        const isFocusable = element.getAttribute('tabindex') !== '-1';
        return !element.disabled && isFocusable;
    });
}
function handleKeydown(event, el, data) {
    // Only handle Tab key for focus trapping. Screen readers also use up and down arrow keys
    // to navigate but it is hanbled by `[role="dialog"]` attribute.
    if (event.key !== 'Tab')
        return;
    // If there are no focusable elements, don't trap focus
    if (data.focusableElements.length === 0) {
        return;
    }
    const currentElement = document.activeElement;
    // Check if the current element is within our container
    if (!el.contains(currentElement)) {
        // If focus is outside our container, focus the first element
        data.focusableElements[0].focus();
        return;
    }
    const currentIndex = data.focusableElements.indexOf(currentElement);
    // If current element is not in our focusable list, focus the first element
    if (currentIndex === -1) {
        data.focusableElements[0].focus();
        return;
    }
    if (event.shiftKey) {
        // Shift + Tab: move backwards
        if (currentIndex === 0) {
            event.preventDefault();
            data.focusableElements[data.focusableElements.length - 1].focus();
        }
    }
    else {
        // Tab: move forwards
        if (currentIndex === data.focusableElements.length - 1) {
            event.preventDefault();
            data.focusableElements[0].focus();
        }
    }
}
/**
 * Vue directive that implements focus trapping functionality for
 * pseduo-modal elements like RemoveCartItemOverlay.
 *
 * It's used mainly for accessibility reasons.
 *
 * @example
 * ```vue
 * <!-- Basic usage -->
 * <div v-focus-trap class="modal">
 *   <button>First</button>
 *   <input type="text" />
 *   <button>Last</button>
 * </div>
 *
 * <!-- With trigger element -->
 * <div v-focus-trap="{ triggerElement: openButton }" class="modal">
 *   <!-- modal content -->
 * </div>
 * ```
 */
const focusTrapDataMap = new WeakMap();
const focusTrap = {
    inserted(el, binding) {
        const data = {
            focusableElements: [],
            boundHandleKeydown: (event) => handleKeydown(event, el, data),
            previousActiveElement: document.activeElement,
            triggerElement: binding.value?.triggerElement || null,
        };
        // Store the data in WeakMap instead of on the element
        focusTrapDataMap.set(el, data);
        // This is important for screen readers like NVDA that use seperate focus
        // that is changed with up and down arrow keys.
        el.setAttribute('role', 'dialog');
        // Set up the focus trap
        data.focusableElements = getFocusableElementsIn(el);
        document.addEventListener('keydown', data.boundHandleKeydown);
        // Enhance dialog usability by focusing a useful first interactive element. Prefer `[autofocus]` when present, then the first input or non-close button, instead of the close button.
        if (data.focusableElements.length > 0) {
            const firstWithAutofocus = data.focusableElements.find((e) => e.hasAttribute('autofocus'));
            const elementToFocus = firstWithAutofocus ??
                el.querySelector('input') ??
                el.querySelector('button:not(.close)') ??
                data.focusableElements[0];
            if (elementToFocus) {
                // Use nextTick to ensure DOM is ready.
                Vue.nextTick(() => {
                    elementToFocus.focus();
                });
            }
        }
    },
    update(el) {
        const data = focusTrapDataMap.get(el);
        if (data) {
            // Update focusable elements (useful when buttons become enabled/disabled)
            data.focusableElements = getFocusableElementsIn(el);
        }
    },
    unbind(el) {
        const data = focusTrapDataMap.get(el);
        if (data) {
            document.removeEventListener('keydown', data.boundHandleKeydown);
            // Restore focus to the trigger element if provided, otherwise use previous element
            const elementToFocus = data.triggerElement || data.previousActiveElement;
            if (elementToFocus) {
                elementToFocus.focus();
            }
            // Clean up the WeakMap entry
            focusTrapDataMap.delete(el);
        }
    },
};
export default focusTrap;
